import { DocumentData } from "firebase/firestore";

export type DashboardRegistration = {
	id: string;
	regID: string;
	status: string;
	paidAt: Date | null;
	session?: string | null;
	practiceTeam?: null | boolean;
};

export type DashboardAthlete = DocumentData & {
	id: string;
	parentID: string;
	firstName?: string;
	lastName?: string;
	season?: string;
	dob?: Date | { toDate: () => Date } | string | number;
	registrations?: DashboardRegistration[];
};

export type RegistrationWithEvent = {
	athlete: DashboardAthlete;
	registration: DashboardRegistration;
	event: DocumentData;
	eventDate: Date | null;
};

export const getVisibleEvents = (events: DocumentData[]) =>
	events.filter((event) => event.hidden !== true);

export const getVisibleRegistrations = (
	athlete: DashboardAthlete,
	visibleEvents: DocumentData[],
) =>
	athlete.registrations?.filter((registration) =>
		visibleEvents.some((event) => event.id === registration.id),
	) ?? [];

const getAthleteDateOfBirth = (athlete: DashboardAthlete) => {
	const { dob } = athlete;
	if (!dob) {
		return null;
	}
	if (dob instanceof Date) {
		return dob;
	}
	if (
		typeof dob === "object" &&
		dob !== null &&
		typeof dob.toDate === "function"
	) {
		return dob.toDate();
	}
	if (typeof dob !== "string" && typeof dob !== "number") {
		return null;
	}
	const parsedDate = new Date(dob);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getAthleteAgeOnEventDate = (
	athlete: DashboardAthlete,
	event: DocumentData,
) => {
	const dob = getAthleteDateOfBirth(athlete);
	const eventDate = getEventDate(event) ?? new Date();
	if (!dob) {
		return null;
	}

	let age = eventDate.getFullYear() - dob.getFullYear();
	const birthdayHasPassed =
		eventDate.getMonth() > dob.getMonth() ||
		(eventDate.getMonth() === dob.getMonth() &&
			eventDate.getDate() >= dob.getDate());

	if (!birthdayHasPassed) {
		age -= 1;
	}

	return age;
};

export const athleteMeetsEventAgeLimits = (
	athlete: DashboardAthlete,
	event: DocumentData,
) => {
	const minAge = Number(event.minAge);
	const maxAge = Number(event.maxAge);
	const hasMinAge =
		event.minAge != null &&
		event.minAge !== "" &&
		Number.isFinite(minAge) &&
		minAge >= 0;
	const hasMaxAge =
		event.maxAge != null &&
		event.maxAge !== "" &&
		Number.isFinite(maxAge) &&
		maxAge >= 0;

	if (!hasMinAge && !hasMaxAge) {
		return true;
	}

	const athleteAge = getAthleteAgeOnEventDate(athlete, event);
	if (athleteAge == null) {
		return false;
	}

	return (
		(!hasMinAge || athleteAge >= minAge) &&
		(!hasMaxAge || athleteAge <= maxAge)
	);
};

export const getAthleteEventAgeLimitReason = (
	athlete: DashboardAthlete,
	event: DocumentData,
) => {
	const minAge = Number(event.minAge);
	const maxAge = Number(event.maxAge);
	const hasMinAge =
		event.minAge != null &&
		event.minAge !== "" &&
		Number.isFinite(minAge) &&
		minAge >= 0;
	const hasMaxAge =
		event.maxAge != null &&
		event.maxAge !== "" &&
		Number.isFinite(maxAge) &&
		maxAge >= 0;

	if (!hasMinAge && !hasMaxAge) {
		return "";
	}

	const athleteAge = getAthleteAgeOnEventDate(athlete, event);
	if (athleteAge == null) {
		return "missing DOB";
	}
	if (hasMinAge && athleteAge < minAge) {
		return "too young";
	}
	if (hasMaxAge && athleteAge > maxAge) {
		return "too old";
	}

	return "";
};

export const getAvailableEvents = (
	athlete: DashboardAthlete,
	visibleEvents: DocumentData[],
) =>
	visibleEvents.filter((event) => {
		const isEligible = event.season === "Either" || event.season === athlete.season;
		const alreadyRegistered = athlete.registrations?.some(
			(registration) => registration.id === event.id,
		);
		return isEligible && !alreadyRegistered;
	});

export const getEventDate = (event: DocumentData) => {
	if (!event.date) {
		return null;
	}

	if (typeof event.date.toDate === "function") {
		return event.date.toDate();
	}

	if (event.date instanceof Date) {
		return event.date;
	}

	const parsedDate = new Date(event.date);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getTodayStart = () => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return today;
};

export const getRegistrationsWithEvents = (
	athletes: DashboardAthlete[],
	visibleEvents: DocumentData[],
) =>
	athletes.flatMap((athlete) =>
		getVisibleRegistrations(athlete, visibleEvents)
			.map<RegistrationWithEvent | null>((registration) => {
				const event = visibleEvents.find((event) => event.id === registration.id);
				return event
					? {
							athlete,
							registration,
							event,
							eventDate: getEventDate(event),
						}
					: null;
			})
			.filter((item): item is RegistrationWithEvent => item !== null),
	);

export const getUnpaidRegistrations = (
	athletes: DashboardAthlete[],
	visibleEvents: DocumentData[],
) =>
	getRegistrationsWithEvents(athletes, visibleEvents).filter(
		(item) => item.registration.status === "unpaid",
	);

export const getVisiblePaidRegistrations = (
	athletes: DashboardAthlete[],
	visibleEvents: DocumentData[],
) => {
	return getRegistrationsWithEvents(athletes, visibleEvents)
		.filter((item) => item.registration.status === "paid")
		.sort(
			(a, b) =>
				(a.eventDate?.getTime() ?? 0) - (b.eventDate?.getTime() ?? 0),
		);
};

export const getTotalVisibleRegistrations = (
	athletes: DashboardAthlete[],
	visibleEvents: DocumentData[],
) =>
	athletes.reduce(
		(total, athlete) =>
			total + getVisibleRegistrations(athlete, visibleEvents).length,
		0,
	);

export const getOpenAvailableOpportunities = (
	athletes: DashboardAthlete[],
	visibleEvents: DocumentData[],
) =>
	athletes.flatMap((athlete) =>
		getAvailableEvents(athlete, visibleEvents)
			.filter((event) => event.open === true)
			.map((event) => ({ athlete, event })),
	);
